import React from 'react'
import styled from 'styled-components'

const MajorHeading = styled.div`
  font-weight: bold;
  font-size: 13pt;
  text-decoration: underline;
  padding: 7pt 0;
`

const MinorHeading = styled.div`
  font-weight: bold;
  font-size: 11pt;
  padding: 7pt 0;
`

const ResourceText = styled.div`
  font-size: 11pt;
  padding: 7pt 0;
`

const PointList = styled.ul`
  margin: 10pt 0 15pt 35pt;
  list-style-type: "-    ";
  font-size: 11pt;
`

const PointListItem = styled.li`
`

const CoachingBestPractices = () => {
  return (
    <>
      <ResourceText>
        The key to a successful coaching business is having successful clients. In this short guide, we&rsquo;ll teach you the best
        practices to follow to ensure your clients are getting results and understanding the value that you deliver to them as a coach.
      </ResourceText>
      <MajorHeading>Step 1: <i>Deliver Value</i></MajorHeading>
      <ResourceText>
        Your clients pay a premium for nutrition coaching, and if they don&rsquo;t quickly see the value you provide, they&rsquo;re not
        going to be a client much longer. Fortunately, there are a variety of ways you can deliver value to your clients through the Avatar
        portal system, even before they start seeing major physical progress. This is generally done by providing accountability, guidance,
        and troubleshooting to ensure they see results.
      </ResourceText>
      <MinorHeading>Provide Accountability</MinorHeading>
      <ResourceText>
        With clients that are new to counting macros, one of the most common points of failure they experience is a failure to log what
        they&rsquo;re eating - and without tracking your food, how could you expect to see the results that come from eating the right
        amount of macros?!? As a coach, you have the ability to see who is slacking and correct that bad habit early on.
      </ResourceText>
      <ResourceText>
        On the &ldquo;client overview&rdquo; page, anyone marked &ldquo;incomplete&rdquo; in the tracking column has missed an entire day
        of tracking. There are also sideways arrows at the column header you can click on to change the view so you can see exactly which
        days were missed. The more you&rsquo;re looking at the overview page, the faster you&rsquo;ll recognize who needs help with what -
        for example, you can also tell who isn&rsquo;t tracking by looking at the tracked averages, and they&rsquo;ll likely all be in the
        blue (showing &ldquo;under range&rdquo;) because day(s) went by showing no food added. You can also go to the &ldquo;clients in need
        of attention&rdquo; area on the overview page, and select for &ldquo;incomplete tracking&rdquo; to pull up everyone who falls into
        that category.
      </ResourceText>
      <ResourceText>
        The faster you jump on this, the better&ndash;don&rsquo;t ever allow multiple consecutive days of not tracking to take place. It&rsquo;s
        up to you to chat with your clients, walk them through their day, and retroactively help them log what they ate. You might also want to
        help them pre-plan what they&rsquo;re going to eat on the following days. Some clients are going to require more assistance than others,
        but for those who are having a hard time with consistently tracking, being there to stay on their case provides an extremely valuable
        service&ndash;accountability.
      </ResourceText>
      <ResourceText>
        We also suggest using the messaging feature within the app to live-chat with your clients when you&rsquo;re reminding them to log what
        they&rsquo;ve eaten or are walking them through planning future days&rsquo; meals. Every interaction with a client adds perceived value
        and helps build trust and rapport, which is a key component of a long-term coaching relationship.
      </ResourceText>
      <MinorHeading>Impart Guidance</MinorHeading>
      <ResourceText>
        Clients unfamiliar with counting macros are going to need guidance upfront. They may struggle at first to get within range of their daily
        targets, and aside from helping them plan meals to better fit their macros, you can provide tremendous value by using the templates feature
        to build an entire day&rsquo;s menu for them. You can then send the meal and day templates you create directly to your client&rsquo;s app
        through the portal, where the client can quickly add them to their food log or save them as &ldquo;favorite meals or days.&rdquo; These
        templates can serve as training wheels to get your client started, and over time they can start making substitutions, alter portion sizes,
        and eventually even do freestyle tracking.
      </ResourceText>
      <ResourceText>
        The added bonus here is that you&rsquo;re also helping clients avoid any potential feelings of failure associated with being out of range
        of their macros the first few weeks while simultaneously helping them see some quick results at their check-ins! Helping your clients gain
        confidence and added motivation through quick progress places them squarely on the path to becoming a successful client.
      </ResourceText>
      <ResourceText>
        Assistance with choosing and navigating goals on the app is another area you&rsquo;ll need to provide guidance. Eventually, it&rsquo;s likely
        that your client will hit their initial goal and you&rsquo;ll need to teach them how to maintain their results while helping them decide
        which goal to choose next. For example, if your client&rsquo;s goal is fat loss and they reach their target weight, we recommend switching
        over to a reverse diet to bring their total macro intake back up&nbsp;from dieting to maintenance levels in a controlled manner. This helps
        prevent weight regain associated with jumping from fat loss macros right back to pre-diet intake. Once the client is eating an amount of food
        they&rsquo;re happy with to maintain their new weight, you would suggest that they change their goal to maintenance and help them select the
        weight range they&rsquo;d like to stay in.
      </ResourceText>
      <ResourceText>
        Navigation through goals should involve conversations with clients about their journey and how they&rsquo;re handling different aspects of it.
        Any barriers should be identified and strategies should be implemented to overcome these. Often, reaching a goal weight is not done in one
        fat loss phase&ndash;instead several fat loss/reverse diet/maintenance phases may need to be completed before a client reaches their target
        weight. The more you communicate with your clients, the better you&rsquo;ll be able to guide them and implement a personalized strategy.
      </ResourceText>
      <MinorHeading>Troubleshoot to Ensure Results</MinorHeading>
      <ResourceText>
        Your clients are coming to you because they believe you can help them accomplish a goal. If the months drag on with no change to their body,
        they&rsquo;re likely to get frustrated and quit. For most people, checking in each week and getting within range of the macros prescribed by
        the system is all that&rsquo;s needed to see results, but there will likely be times when you&rsquo;ll need to do some investigation as to
        why the results aren&rsquo;t coming.
      </ResourceText>
      <ResourceText>
        We love using the &ldquo;peanut butter&rdquo; example to show how loose tracking and poor estimation can interfere with results. Peanut butter
        is <i>very</i>&nbsp;high in fat content and has a lot of calories, so it&rsquo;s critical that portion sizes are measured and logged accurately.
        All too often, people use a kitchen spoon to scoop peanut butter onto a piece of bread or into a bowl of oats, and then track it as &ldquo;1 tablespoon.&rdquo;
        But if you were to measure out a tablespoon of peanut butter and compare it to a kitchen spoon of peanut butter, you&rsquo;d see that there&rsquo;s
        about 3x as much peanut butter on the kitchen spoon!
      </ResourceText>
      <ResourceText>
        These &ldquo;hidden&rdquo; calories that go unaccounted for will stall progress, and all the while clients believe they&rsquo;re eating what they
        should to get results. In reality improper tracking may have them eating at, or even above, maintenance levels. Peanut butter isn&rsquo;t the only
        culprit&ndash;when you open a client&rsquo;s tracker and scan through what they&rsquo;ve been adding, you&rsquo;re likely to encounter lots of food
        items that could warrant further examination. Playing Sherlock Holmes inside a client&rsquo;s tracker will often yield valuable tidbits of information
        you can use to help them troubleshoot and start seeing the progress they came to you for!
      </ResourceText>
      <ResourceText>
        Beyond just looking at their tracker, you can also look at their &ldquo;timeline table&rdquo; to get an in-depth explanation of everything the macro
        adjusting algorithm is doing on the back end. You&rsquo;ll see why adjustments are (or aren&rsquo;t) being made and additional, more detailed information
        about the client&rsquo;s check-ins and setting changes they&rsquo;ve made on the app that would impact macros and calories (such as updating activity level,
        vacation mode, etc.). When your clients have questions about why their macros were increased or decreased, you can always refer to this table for the answer!
      </ResourceText>
      <MajorHeading>Step 2: Use the App Yourself (<i>Practice What You Preach</i>)</MajorHeading>
      <ResourceText>
        Using the Avatar app to track macros yourself will serve you well in several key ways:
      </ResourceText>
      <PointList>
        <PointListItem>
          By using the app personally and experiencing the convenience and results firsthand, you&rsquo;ll find it incredibly easy to sell potential clients
          on the idea of working with you as a coach using the system.
        </PointListItem>
        <PointListItem>
          Being familiar with the app will allow you to easily troubleshoot any technical issues your clients run into, while also helping you identify exactly
          what features would solve the challenges they face (such as using the &lsquo;edit balance&rsquo; tool on a day when the proportion of carbs and fats
          gets way out of whack).
        </PointListItem>
        <PointListItem>
          If your clients add you as a &ldquo;friend&rdquo; on the app, you can easily share recipes and view each other&rsquo;s trackers, which also provides
          you an opportunity to lead by example and gain personal accountability for yourself from your clients!
        </PointListItem>
        <PointListItem>
          Over time, you&rsquo;ll have a chance to try out all the goal settings yourself and will be able to use your experience to better guide
          your clients through the goals.
        </PointListItem>
        <PointListItem>
          &ldquo;Do as I say, not as I do&rdquo; might be the biggest way to demotivate anyone who is seeking your leadership, so instead, practice
          what you preach&ndash;clients will respect this.
        </PointListItem>
      </PointList>
      <MajorHeading>Step 3: <i>Look at Your Client Overview EVERY DAY</i></MajorHeading>
      <ResourceText>
        If you&rsquo;re only checking your portal dashboard sporadically or once a week, you&rsquo;re setting both yourself and your clients up for failure. You need to
        log onto the portal DAILY because the &ldquo;coachable&rdquo; moments of life happen on a daily basis. Logging on daily means that you&rsquo;ll immediately see
        when someone needs help and will be able to address the situation while it&rsquo;s still relevant. Waiting too long to look at the client overview could be the
        difference between a client seeing great success and turning into a lifelong customer and source of referrals, and the same client getting frustrated and quitting.
      </ResourceText>
      <ResourceText>
        Just think about the &ldquo;incomplete tracking&rdquo; indicator&ndash;if you see this appear it will be for the previous day, and if you&rsquo;re checking the
        overview daily your client will probably still be able to remember most of what they ate the day before. You&rsquo;ll help them fill in the gaps, and then make
        sure they&rsquo;re tracking their food today. Building the right habits early on makes coaching the same client much easier in the long run.
      </ResourceText>
      <ResourceText>
        Getting yourself in the habit of checking your overview daily and keeping a close eye on your clients will make you a much better coach and give your clients
        the best possible chance of success working with you.
      </ResourceText>
      <ResourceText>
      </ResourceText>
      <MajorHeading><i>Pro Tips:</i></MajorHeading>
      <MinorHeading>Set client check-ins to Friday</MinorHeading>
      <ResourceText>
        <i>By having check-ins on Friday, you&rsquo;re allowing clients to go a little overboard on the weekends (which is common) and they&rsquo;ll still have the work
        week to get their averages back where they need to be.</i>
      </ResourceText>
      <MinorHeading>Don&rsquo;t turn on daily macro adjustments until clients are tracking <i>everything</i></MinorHeading>
      <ResourceText>
        <i>The Daily Macro Adjustment feature takes any carbs and fats that were over or under the range for that day and distributes them to the remaining days
        in a check-in period. It&rsquo;s like an &ldquo;easy button&rdquo; for staying in range if your clients are tracking everything, but if they miss days
        or forget to log meals, it&rsquo;s going to artificially increase the targets beyond what they should be.</i>
      </ResourceText>
      <ResourceText>
        <i>Make sure to keep this feature off until you KNOW your clients are tracking everything like they&rsquo;re supposed to.</i>
      </ResourceText>
      <MinorHeading>Start your newbies&nbsp;on &ldquo;Vacation Mode&rdquo; so they can learn the app without pressure</MinorHeading>
      <ResourceText>
        <i>Turning on the Vacation Mode feature turns off the countdown timer to the first check-in and also provides the option to hide the recommended macro
        target ranges. This allows new clients to practice tracking and logging food without the pressure of trying to hit the macro targets out of the gate.
        Once clients are comfortable tracking, vacation mode can be turned off and the system will collect a new starting weight and restart the clock to
        the client&rsquo;s first check-in.</i>
      </ResourceText>
      <MinorHeading>Turn on daily weights for clients who can weigh themselves regularly</MinorHeading>
      <ResourceText>
        <i>The Daily Weights feature is great for washing out errors caused by day-to-day water weight fluctuations, providing a more accurate picture of weight
        trends over time than if a client were to only enter a weight on check-in day. Two added benefits of using this feature is that it encourages a client
        to get in the habit of opening the app every day, which increases the odds that they&rsquo;ll also track their food every day, and also that it teaches
        the client not to be so obsessed with small changes in the scale that are usually due to changes in water rather than fat.</i>
      </ResourceText>
      <MinorHeading>Set small milestones and celebrate your clients&rsquo; victories often</MinorHeading>
      <ResourceText>
        <i>You&rsquo;ll see your clients hit milestones such as 10 pounds of weight lost or a week of tracking with all macros in-range&ndash;these are moments
        worth celebrating because they&rsquo;re signs of a successful coaching experience! Be sure to acknowledge and remind clients of their progress and keep
        them motivated by cheering on their victories. It shouldn&rsquo;t be all constructive criticism all the time&hellip;throw in a little praise and see
        your clients eyes light up.</i>
      </ResourceText>
    </>
  )
}

export default CoachingBestPractices 